import React, { useEffect, useRef } from 'react';
import {
  HubConnectionBuilder,
  LogLevel,
  HttpTransportType,
  HubConnectionState
} from '@microsoft/signalr';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { addInfo, addError, deleteInfoByText } from '../../actions/snackbar/snackbar';
import { getInvitations } from '../../actions/userManagement/userManagementActions';
import { getScenarios } from '../../actions/scenarios/scenarioAction';
import { CurrentUserSelector } from '../../selectors/userSelectors';
import type { UserNotificationDto } from '../../../types/realtime';
import { getUrlFromSwaggerUrl } from '../../apis/api';
import { I18nNamespace } from '../../../i18n/types/i18nNamespace';
import type { I18nCommonNs } from '../../../i18n/dictionaries/interfaces';
import { createAccessTokenProvider } from '@/app/services/authToken';
import { authTelemetry } from '@/app/services/authTelemetry';
import { apiLoginRequest, clearLogoutFlag, hasUserLoggedOut } from '@/authConfig.ts';

/** Resolved hub URL: "<api-base>/hubs/notifications" */
const getWebsocketUrl = (): string =>
  `${getUrlFromSwaggerUrl(process.env.REACT_APP_SCENES)}/hubs/notifications` || '';

/**
 * Provides a single SignalR connection that lives for the whole
 * authenticated session.  Children don't need the connection object,
 * so nothing is exposed – the provider only handles side effects.
 */
const NotificationHubProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(I18nNamespace.Common);
  const currentUser = useSelector(CurrentUserSelector);
  const { instance } = useMsal();
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    /** Abort when the user is not yet known (or logged out). */
    if (!currentUser?.id) return;

    const accessTokenProvider = createAccessTokenProvider({
      msalInstance: instance,
      apiLoginRequest,
      authTelemetry,
      hasUserLoggedOut,
      clearLogoutFlag
    });

    const connection = new HubConnectionBuilder()
      .configureLogging(LogLevel.Warning)
      .withUrl(getWebsocketUrl(), {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
        accessTokenFactory: () =>
          accessTokenProvider().catch((err) => {
            console.warn('[NOTIFICATION.HUB] failed to acquire access token', err);
            return '';
          })
      })
      .withAutomaticReconnect()
      .build();

    
    connection.keepAliveIntervalInMilliseconds = 1000 * 15; // 15 seconds
    connection.serverTimeoutInMilliseconds    = 1000 * 60 * 20; // 20 minutes

    /** Helper to extract server message from payload */
    const getServerMessage = (payload: unknown, fallback: string): string => {
      return payload && 
        typeof payload === 'object' && 
        'Message' in payload && 
        typeof (payload as { Message: unknown }).Message === 'string'
        ? (payload as { Message: string }).Message
        : fallback;
    };

    /** Maps backend notification → toast text. */
    const handleNotification = (msg: UserNotificationDto) => {
      const prefix =
        msg.status === 'Completed' ? '✅' : msg.status === 'Failed' ? '❌' : 'ℹ️';
      // Human-friendly "completed / failed / …" (lower-case first letter)
      const statusText = msg.status.charAt(0).toLowerCase() + msg.status.slice(1);

      // Handle failures first for all task types
      if (msg.status === 'Failed') {
        const serverMessage = getServerMessage(msg.payload, `${msg.taskType} failed`);
        const errorText = `${prefix} ${serverMessage}`;
        dispatch(addError({ errorText }));
        
        // Clean up any related "sending" info messages
        if (msg.taskType === 'InviteUser') {
          dispatch(deleteInfoByText({ infoText: 'Sending invitation' }));
        }
        
        return;
      }

      let infoText = '';
      switch (msg.taskType) {
        case 'InviteUser': {
          let userEmail = '';
          if (
            msg.payload &&
            typeof msg.payload === 'object' &&
            'Email' in msg.payload &&
            typeof (msg.payload as { Email: unknown }).Email === 'string'
          ) {
            userEmail = (msg.payload as { Email: string }).Email;
          }
          infoText = `${prefix} ${t('userInvitation', { email: userEmail })}`;
          
          // Refresh invitations table when invitation is completed successfully
          if (msg.status === 'Completed') {
            dispatch(getInvitations());
          }
          break;
        }
        case 'CopyScenario': {
          console.log('CopyScenario finished', msg.payload);
          
          let scenarioName = '';
          let scenarioId = '';
          if (
            msg.payload &&
            typeof msg.payload === 'object' &&
            'ScenarioName' in msg.payload &&
            typeof (msg.payload as { ScenarioName: unknown }).ScenarioName === 'string' &&
            'ScenarioId' in msg.payload &&
            (typeof (msg.payload as { ScenarioId: unknown }).ScenarioId === 'string' ||
             typeof (msg.payload as { ScenarioId: unknown }).ScenarioId === 'number')
          ) {
            scenarioName = (msg.payload as { ScenarioName: string }).ScenarioName;
            scenarioId = String((msg.payload as { ScenarioId: string | number }).ScenarioId);
          }
          console.log('Extracted scenarioId:', scenarioId, 'scenarioName:', scenarioName);
          const scenarioUrl = `${window.location.origin}/scene/${scenarioId}`;
          console.log('Generated scenarioUrl:', scenarioUrl);
          infoText = `${t(nameof.full<I18nCommonNs>((n) => n.scenarioCopyReady), { scenarioName })}`;
          
          // Refresh scenario list when copy is completed successfully
          if (msg.status === 'Completed') {
            dispatch(getScenarios());
          }
          
          dispatch(addInfo({ 
            infoText, 
            link: scenarioUrl, 
            linkText: scenarioName
          }));
          return; // Early return to avoid the generic dispatch at the end
        }
        // Example: Add specific cases based on known task types and payload structure
        // case 'ScenarioProcessing': {
        //   // Assuming payload is relevant data, e.g., scenario name or ID
        //   const scenarioInfo = typeof msg.payload === 'string' ? ` (${msg.payload})` : '';
        //   infoText = `${prefix} Scenario processing ${statusText}${scenarioInfo}`;
        //   break;
        // }
        // Add more cases as needed for different task types...

        default:
          // Fallback to the generic message format
          infoText = `${prefix} ${msg.taskType} ${statusText}`;
          break;
      }

      dispatch(addInfo({ infoText }));
    };

    connection.on('UserNotification', handleNotification);

    /* Connection life-cycle ------------------------------------------------ */
    const start = async (): Promise<void> => {
      try {
        await connection.start();
      } catch (err) {
        console.error('[NOTIFICATION.HUB] start failed', err);
      }
    };
    start();

    // If we stay in "Reconnecting" for 30 s → warn user.
    connection.onreconnecting(() => {
      reconnectTimer.current = setTimeout(() => {
       // dispatch(addInfo({ infoText: 'Live updates unavailable' }));
      }, 30_000);
    });

    connection.onreconnected(() => {
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = undefined;
      }
      //dispatch(addInfo({ infoText: 'Live updates restored' }));
    });

    // Clean-up on unmount / logout
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      // Stop gracefully; ignore failures during teardown.
      connection
        .stop()
        .catch((err) => console.debug('[NOTIFICATION.HUB] stop error', err));
    };
  }, [currentUser?.id, dispatch, instance]);

  return <>{children}</>;
};

export default NotificationHubProvider;
